import * as angular from 'angular';
import { Component, EventEmitter, Input,  Output, Inject } from 'ng-metadata/core';

import './sentiment.scss';

export enum SentimentValue {
    P = 'Positive',
    N = 'Neutral',
    V = 'Negative',
    U = 'Not Rated'
}

/**
 * @ngdoc directive
 * @name mwContentDocumentSentiment
 * @module mw.components.contentDocuments
 * @param {string} [sentiment] - The current sentiment of the document.
 * @param {string} [sentiment-override] - The sentiment of the document, if its ever been overwritten by the user
 * @param {boolean} [is-inline-sentiment-enabled=false] - EXPERIMENTAL: Enable new sentiment selector (only in desktop)
 * @param {expression} [on-sentiment-update] - Function passed in to handle sentiment when it updates.
 * @param {expression} [on-sentiment-reset] - Function passed in to handle sentiment when the user resets it.
 *  @description
 * `mw-content-document-sentiment` is responsible for showing a specific documents sentiment and providing the user a menu to change the sentiment as needed.
 * @usage
 * <hljs hls-language="html">
 *  <mw-content-document-sentiment current-sentiment="$ctrl.document.sentiment" is-sentiment-modified="$ctrl.document.sentiment"></mw-content-document-sentiment>
 * </hls>
 */
@Component({
    selector: 'mw-content-document-sentiment',
    template: `
            <md-menu class="mw-menu mw-content-document-sentiment__menu" md-position-mode="target-left target" md-offset="0 50" ng-if="::($ctrl.isMobile || $ctrl.isTablet ||  !$ctrl.isInlineSentimentEnabled)">
                <md-menu-trigger-button class="mw-content-document-sentiment__menu-trigger {{$ctrl.sentimentClass}}"  ng-click="$ctrl.handleSentimentClick($event)" tooltip="{{'Open Menu' | translate}}">{{$ctrl.sentimentName | translate}}</md-menu-trigger-button>
                <md-menu-content class="mw-menu-dropdown" md-padding="md-padding">
                </md-menu-content>
            </md-menu>
            <div class="mw-content-document-sentiment__inline" ng-if="::$ctrl.isDesktop && $ctrl.isInlineSentimentEnabled" flex>
                <span class="mw-content-document-sentiment__inline-value {{$ctrl.sentimentClass}}" ng-mouseover="$ctrl.handleInlineHover($event)" ng-mouseleave="$ctrl.enableSelector()" ng-click="$ctrl.handleInlineClick()">{{$ctrl.sentimentName | translate}}</span>
            </div>
    `
})
export class SentimentComponent {
    @Input('<') sentiment: string;
    @Input('<') sentimentOverride: string;
    @Input('<') isInlineSentimentEnabled: boolean = false;

    @Output() onSentimentUpdate = new EventEmitter<string>();
    @Output() onSentimentReset = new EventEmitter();

    sentimentPanelRef: ng.material.IPanelRef;
    isDesktop: boolean;
    isMobile: boolean;
    isTablet: boolean;
    canShowSelector: boolean = true;

    constructor(
        @Inject('$mdPanel') private $mdPanel: ng.material.IPanelService,
        @Inject('$timeout') private $timeout: ng.ITimeoutService,
        @Inject('mwEnvDetector') private mwEnvDetector) {
        'ngInject';

        this.isDesktop = mwEnvDetector.isDesktop;
        this.isMobile = mwEnvDetector.isMobile;
        this.isTablet = mwEnvDetector.isTablet;
    }

    get sentimentName(): string {
        return this.sentimentOverride ? SentimentValue[this.sentimentOverride] : SentimentValue[this.sentiment];
    }

    get sentimentClass() {
        let sentimentClass = 'mw-content-document-sentiment__' + (this.sentimentOverride || this.sentiment).toLowerCase();

        if (this.sentimentOverride) {
            sentimentClass = sentimentClass + '--modified';
        }
        return sentimentClass;
    }

    handleSentimentUpdate(sentiment: string) {
        this.closeSentimentMenu();
        this.onSentimentUpdate.emit(sentiment);
    }

    handleSentimentReset() {
        this.closeSentimentMenu();
        this.onSentimentReset.emit({});
    }

    handleSentimentClick($event) {
        this.openSentimentMenu($event);
    }

    openSentimentMenu($event) {
        let position = this.$mdPanel.newPanelPosition()
            .relativeTo($event.currentTarget)
            .addPanelPosition(this.$mdPanel.xPosition.ALIGN_START, this.$mdPanel.yPosition.BELOW);

        let config: ng.material.IPanelConfig = {
            attachTo: angular.element(document.body),
            controller: () => this,
            controllerAs: '$ctrl',
            panelClass: 'md-whiteframe-z2',
            position: position,
            template: `<mw-content-document-sentiment-menu sentiment="$ctrl.sentiment" sentiment-override="$ctrl.sentimentOverride" on-sentiment-update="$ctrl.handleSentimentUpdate($event)" on-sentiment-reset="$ctrl.handleSentimentReset()"></mw-content-document-sentiment-menu>`,
            clickOutsideToClose: true,
            escapeToClose: true,
            focusOnOpen: true,
            onDomRemoved: () => {
                if (this.sentimentPanelRef) {
                    this.sentimentPanelRef.destroy();
                    this.sentimentPanelRef = null;
                }
            }
        }

        this.$mdPanel.open(config).then((panelRef) => {
            this.sentimentPanelRef = panelRef;
        });
    }

    openSentimentSelector($event) {
        let position = this.$mdPanel.newPanelPosition().absolute();

        let config: ng.material.IPanelConfig = {
            attachTo: angular.element($event.target.parentElement),
            controller: () => this,
            controllerAs: '$ctrl',
            panelClass: 'mw-content-document-sentiment-selector__panel',
            position: position,
            template: `<mw-content-document-sentiment-selector
                            sentiment="$ctrl.sentiment"
                            sentiment-override="$ctrl.sentimentOverride"
                            on-sentiment-update="$ctrl.handleSentimentUpdate($event)"
                            on-sentiment-reset="$ctrl.handleInlineSentimentReset()"
                            ng-mouseleave="$ctrl.handleInlineMouseLeave()"></mw-content-document-sentiment-selector>`,
            clickOutsideToClose: false,
            escapeToClose: false,
            focusOnOpen: false,
            onDomRemoved: () => {
                if (this.sentimentPanelRef) {
                    this.sentimentPanelRef.destroy();
                    this.sentimentPanelRef = null;
                }
            }
        }

        this.$mdPanel.open(config).then((panelRef) => {
            this.sentimentPanelRef = panelRef;
        });
    }

    closeSentimentMenu() {
        if (this.sentimentPanelRef) {
            this.sentimentPanelRef.close();
        }
    }

    handleInlineSentimentReset() {
        this.handleSentimentReset();
        this.handleInlineMouseLeave();
    }

    handleInlineClick() {
        if (!this.sentimentPanelRef) {
            this.canShowSelector = false;

            this.openSentimentSelector(event);
        }
    }

    handleInlineHover(event) {

        if (this.canShowSelector) {
            this.canShowSelector = false;
            this.openSentimentSelector(event);
        }
    }

    handleInlineMouseLeave() {
        if (this.sentimentPanelRef) this.sentimentPanelRef.close();
        this.canShowSelector = true;
    }

    enableSelector() {
        if (!this.sentimentPanelRef) this.canShowSelector = true;
    }

}



// WEBPACK FOOTER //
// ./src/lib/components/content-documents/media-document/sentiment/sentiment.component.ts